import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Home, Mail, Phone, MapPin, Briefcase, User, Building2, Shield } from "lucide-react";
import Since1998Badge from "@/components/Since1998Badge";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseInquiry = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferredContact: "email", // email, phone, sms
    marketInterested: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/franchise-inquiry`, formData);
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Request received! We'll contact you within 24 hours with franchise details.");
      }
    } catch (error) {
      console.error("Error submitting franchise inquiry:", error);
      toast.error("Failed to submit. Please try again or email us at franchise@its-sold.com");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-2xl p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Received!</h2>
          <p className="text-lg text-gray-700 mb-4">
            Thank you for your interest in Its-Sold.com franchise opportunities!
          </p>
          <p className="text-gray-600 mb-6">
            {formData.preferredContact === 'email' && `We'll send franchise details to ${formData.email} within 24 hours.`}
            {formData.preferredContact === 'phone' && `We'll call you at ${formData.phone} within 24 hours.`}
            {formData.preferredContact === 'sms' && `We'll text you at ${formData.phone} within 24 hours.`}
          </p>
          <p className="text-gray-600 mb-6">
            Our franchise package includes:
          </p>
          <ul className="text-left text-gray-700 space-y-2 mb-8 max-w-md mx-auto">
            <li className="flex items-start">
              <span className="text-orange-600 mr-2">✓</span>
              <span>Investment requirements & ROI projections</span>
            </li>
            <li className="flex items-start">
              <span className="text-orange-600 mr-2">✓</span>
              <span>Territory availability & market analysis</span>
            </li>
            <li className="flex items-start">
              <span className="text-orange-600 mr-2">✓</span>
              <span>Complete technology platform details</span>
            </li>
            <li className="flex items-start">
              <span className="text-orange-600 mr-2">✓</span>
              <span>Training & ongoing support structure</span>
            </li>
          </ul>
          <p className="text-sm text-gray-500 mb-6">
            Questions? Contact us at franchise@its-sold.com or call (817) 320-5334
          </p>
          <Button onClick={() => navigate('/')} variant="outline">Return to Homepage</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                Its-Sold.com<sup className="text-xs">™</sup>
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Since1998Badge variant="header" />
              <Button onClick={() => navigate('/')} variant="ghost">Home</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-16 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block mb-6">
            <Since1998Badge />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Its-Sold.com Franchise Opportunities
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-4">
            Bring America's Original FSBO Platform to Your Market
          </p>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Join a 27-year legacy. Launch a proven FSBO platform in your city with our complete white-label solution, technology, training, and ongoing support.
          </p>
        </div>
      </div>

      {/* Lead Capture Form */}
      <div className="max-w-3xl mx-auto px-4 pb-20">
        <Card className="p-8 sm:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-orange-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Request Franchise Information
            </h2>
            <p className="text-gray-600">
              Fill out this quick form and we'll send you detailed franchise information within 24 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="space-y-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(214) 555-1234"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Best Way to Contact You <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.preferredContact === 'email' ? 'border-orange-600 bg-orange-50' : 'border-gray-300 hover:border-orange-300'}`}>
                    <input
                      type="radio"
                      name="preferredContact"
                      value="email"
                      checked={formData.preferredContact === 'email'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <Mail className="w-6 h-6 mx-auto mb-1 text-orange-600" />
                      <span className="text-sm font-medium">Email</span>
                    </div>
                  </label>
                  
                  <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.preferredContact === 'phone' ? 'border-orange-600 bg-orange-50' : 'border-gray-300 hover:border-orange-300'}`}>
                    <input
                      type="radio"
                      name="preferredContact"
                      value="phone"
                      checked={formData.preferredContact === 'phone'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <Phone className="w-6 h-6 mx-auto mb-1 text-orange-600" />
                      <span className="text-sm font-medium">Call</span>
                    </div>
                  </label>
                  
                  <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.preferredContact === 'sms' ? 'border-orange-600 bg-orange-50' : 'border-gray-300 hover:border-orange-300'}`}>
                    <input
                      type="radio"
                      name="preferredContact"
                      value="sms"
                      checked={formData.preferredContact === 'sms'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <Briefcase className="w-6 h-6 mx-auto mb-1 text-orange-600" />
                      <span className="text-sm font-medium">Text</span>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Market/City Interested In <span className="text-gray-400 text-sm">(optional)</span>
                </label>
                <Input
                  type="text"
                  name="marketInterested"
                  value={formData.marketInterested}
                  onChange={handleChange}
                  placeholder="e.g., Dallas, Houston, Austin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information <span className="text-gray-400 text-sm">(optional)</span>
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your background, timeline, or any questions..."
                  rows="4"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg py-6"
              >
                {submitting ? "Submitting..." : "Request Franchise Information →"}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-3">
                We respect your privacy. Your information will only be used to send you franchise details.
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default FranchiseInquiry;
